import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ActionType } from 'react-table';

import { NAV_AND_SUBNAV_HEIGHT } from '~/common/constants/page';
import { getSearchConstants } from '~/common/constants/search';
import { ExportExcel } from '~/components/Shared/ExportExcel/ExportExcel';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Table } from '~/eds/src/modules/Table/Table';
import { Action } from '~/eds/src/types/Action';
import { TableState } from '~/eds/src/types/Table';
import { FieldId } from '~/evifields/src/types/FieldId';
import { SearchQuery } from '~/features/advanced-search/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useEvisearchColumns } from '~/hooks/useEvisearchColumns';
import {
  SearchItem,
  SearchResult,
  SearchType,
} from '~/redux/api/methods/evisearch';
import { SearchFilter } from '~/types/DashboardV2';

import { getStaticColumns } from './ResultsTable.utils';

type Props = {
  data?: SearchResult;
  entity: SearchType;
  id?: string;
  enablePersistedTable?: boolean;
  isLoading?: boolean;
  query: SearchQuery;
  searchFilters: Record<FieldId, SearchFilter>;
  state: Partial<TableState>;

  getBulkActions?: (tableState: TableState) => Action[];
  onDocumentNameClick?: (row: SearchItem) => void;
  onNavigate?: (handlerId: number) => void;
  onRowDetailsClick?: (row: SearchItem) => void;
  onSaveView?: () => void;
  onLoadView?: () => void;
  onSetColumnOrder?: (columnOrder: string[]) => void;
  onUpdateState: (state: TableState, action?: ActionType) => void;
};

export const ResultsTable = ({
  data,
  entity,
  id = 'default-search-table-id',
  enablePersistedTable,
  isLoading,
  query,
  searchFilters,
  state,

  getBulkActions,
  onDocumentNameClick,
  onNavigate,
  onRowDetailsClick,
  onLoadView,
  onSaveView,
  onSetColumnOrder,
  onUpdateState,
}: Props) => {
  const TableComponent = enablePersistedTable ? PersistedTable : Table;
  const enableSelectRows = entity === 'document';
  const intl = useIntl();
  const staticColumns = getStaticColumns(
    entity,
    onDocumentNameClick,
    onNavigate,
  );
  const { columns, columnGroups } = useEvisearchColumns({
    staticColumns,
    searchFilters,
  });
  const [showExportExcel, setShowExportExcel] = useState(false);
  const hasExportExcel = useFlag(FlagType.DashboardsExportExcel);

  const { EXPORT_DEFAULT_TEXT } = getSearchConstants(intl);

  // TODO include: const isDuplicateExport = data?.meta?.is_duplicate_export;
  const actions = hasExportExcel
    ? [
        {
          icon: 'file-spreadsheet',
          label: EXPORT_DEFAULT_TEXT,
          disabled: false,
          onClick: () => {
            setShowExportExcel(true);
          },
        },
      ]
    : [];

  const placeholderContent =
    data && !data.results?.length
      ? {
          title: intl.formatMessage({
            id: 'ResultsTable.noRecordsFound',
            defaultMessage: 'No records found',
            description: 'Message when no search results are found',
          }),
        }
      : undefined;

  return (
    <ContentContainer placeholderContent={placeholderContent}>
      <>
        <TableComponent
          context={`ResultsTable:${id}`}
          name={intl.formatMessage({
            id: 'ResultsTable.searchResultName',
            defaultMessage: 'search result',
            description: 'Name for search result table',
          })}
          totalCount={data?.meta?.total ?? 0}
          columns={columns}
          columnGroups={columnGroups}
          data={data?.results ?? []}
          actions={actions}
          isLoading={isLoading}
          state={state}
          getBulkActions={getBulkActions}
          onUpdate={onUpdateState}
          onSetColumnOrder={onSetColumnOrder}
          rowDetails={
            onRowDetailsClick ? { onClick: onRowDetailsClick } : undefined
          }
          onLoadView={onLoadView}
          onSaveView={onSaveView}
          options={{
            enableExportXlsx: false,
            enablePageSizeSelect: true,
            enableSelectRows,
            enableStickyHeader: true,
            stickyHeaderOffset: NAV_AND_SUBNAV_HEIGHT,
          }}
          reactTableOptions={{
            autoResetSortBy: false,
            disableSortRemove: true,
            manualSortBy: true,
          }}
        />
        <ExportExcel
          entity={entity}
          query={query}
          isVisible={showExportExcel}
          onHide={() => setShowExportExcel(false)}
          totalDocs={data?.meta?.total}
          totalDupsAndDocs={data?.meta?.total}
          selectedFieldIds={state.columnOrder}
        />
      </>
    </ContentContainer>
  );
};
